import React, { useCallback, useImperativeHandle, useState } from 'react'
import PropTypes from 'prop-types'
import RoundedModal from '../../../molecules/RoundedModal'
import Loading from '../../../molecules/Loading'
import SydButton from '../../../commonDesign/Button'
import SydModalActions from '../../../commonDesign/SydModal/SydModalActions'
import { useScanLambdaTargets } from '../../../../api/scheduler'

const IDLE = 'idle'
const SCANNING = 'scanning'
const DONE = 'done'
const ERROR = 'error'

const ScanDialog = React.forwardRef(function ScanDialog ({ onComplete }, ref) {
  const [state, setState] = useState({ phase: IDLE, open: false, result: null, error: null })
  const { mutateAsync: scanTargets } = useScanLambdaTargets()

  const close = useCallback(() => {
    setState({ phase: IDLE, open: false, result: null, error: null })
  }, [])

  const run = useCallback(async () => {
    setState({ phase: SCANNING, open: true, result: null, error: null })
    try {
      const result = await scanTargets()
      setState({ phase: DONE, open: true, result, error: null })
      onComplete?.()
    } catch (err) {
      setState({ phase: ERROR, open: true, result: null, error: err?.message ?? 'An unknown error occurred' })
    }
  }, [scanTargets, onComplete])

  useImperativeHandle(ref, () => ({ run }), [run])

  const title = state.phase === ERROR ? 'Scan Failed' : 'Scan Lambda Targets'

  return (
    <RoundedModal
      title={title}
      open={state.open}
      onClose={state.phase === SCANNING ? undefined : close}
      size='medium'
      position='top'
      disablePortal={false}
    >
      <div>
        {state.phase === SCANNING && (
          <div style={{ textAlign: 'center', padding: '1rem 0' }}>
            <Loading size={32} />
            <p>Scanning Lambda functions for target tags&hellip;</p>
          </div>
        )}

        {state.phase === DONE && state.result && (
          <div>
            <p>Scan complete.</p>
            <ul style={{ margin: '0.5rem 0', paddingLeft: '1.25rem' }}>
              <li><strong>{state.result.scanned}</strong> Lambda functions scanned</li>
              <li><strong>{state.result.matched}</strong> targets matched</li>
              <li><strong>{state.result.updated?.length ?? 0}</strong> ARNs updated</li>
            </ul>
            {state.result.updated?.length > 0 && (
              <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '0.75rem', fontSize: '0.875rem' }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', paddingBottom: '0.25rem', borderBottom: '1px solid #ddd' }}>Target</th>
                    <th style={{ textAlign: 'left', paddingBottom: '0.25rem', borderBottom: '1px solid #ddd' }}>Resolved ARN</th>
                  </tr>
                </thead>
                <tbody>
                  {state.result.updated.map((u) => (
                    <tr key={u.code}>
                      <td style={{ padding: '0.25rem 0.5rem 0.25rem 0' }}>{u.code}</td>
                      <td style={{ padding: '0.25rem 0', wordBreak: 'break-all' }}>{u.arn}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {state.phase === ERROR && (
          <p style={{ color: '#c62828' }}>{state.error}</p>
        )}

        {state.phase !== SCANNING && (
          <SydModalActions>
            <SydButton variant='primary' size='md' onClick={close}>
              Close
            </SydButton>
          </SydModalActions>
        )}
      </div>
    </RoundedModal>
  )
})

ScanDialog.propTypes = {
  onComplete: PropTypes.func
}

export default ScanDialog
